import { useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';

export type ScopePhase = 'auth' | 'navigator' | 'extractor' | 'preflight' | 'canary';
export type ScopePhaseStatus = 'pending' | 'started' | 'completed' | 'failed';

// Legacy v0.1 step names (kept for back-compat)
export type ScopeStepName = 'connect' | 'login' | 'navigate' | 'extract' | 'generate' | 'review';
export type ScopeStepStatus = 'pending' | 'running' | 'done' | 'error';

export interface ScopeScreenshot {
  step?: ScopeStepName;
  phase?: ScopePhase;
  url: string;
  caption?: string;
}

export interface ScopeLogLine {
  line: string;
  id: number;
}

export interface ScopeAction {
  phase: ScopePhase;
  action: string;
  intent: string;
  decision: 'allow' | 'deny';
  reason?: string;
  id: number;
}

export interface ScopeBudget {
  aiCalls: number;
  durationMs: number;
  approxCostUsd: number;
  remaining: { minutes: number; aiCalls: number; usd: number };
}

export interface CoverageItem {
  type: string;
  status: 'pending' | 'found' | 'not_exposed';
  endpointName?: string | null;
}

export type CacheEndpointStatus = 'started' | 'progress' | 'ok' | 'failed' | 'rescope_triggered' | 'auth_failed';

export interface CacheEndpointInfo {
  status: CacheEndpointStatus;
  page: number;
  recordsFetched: number;
  durationMs?: number;
  error?: string;
  // Canonical results merged in once they arrive (canonical.endpoint.finished)
  canonicalOkCount?: number;
  canonicalErrCount?: number;
  resourceType?: string;
  // Tail of recent HTTP requests for this endpoint (most-recent first).
  // Bounded to ~10 entries to keep the UI memory cheap.
  recentRequests?: CacheRequestEvent[];
  totalRequests?: number;
  totalBytes?: number;
}

export interface CacheRequestEvent {
  id: number;
  ts: number;
  method: string;
  url: string;
  status: number;
  durationMs: number;
  bytes: number;
  page: number;
  recordsFetched: number;
}

export interface SessionCompletedSummary {
  rawRecordCount: number;
  canonicalRecordCount: number;
  invalidRecordCount: number;
  durationMs: number;
}

export interface UseScopeStreamResult {
  // v0.2 phases
  phases: Record<ScopePhase, ScopePhaseStatus>;
  // v0.1 legacy steps
  steps: Record<ScopeStepName, ScopeStepStatus>;
  screenshots: ScopeScreenshot[];
  logs: ScopeLogLine[];
  actions: ScopeAction[];
  budget: ScopeBudget | null;
  coverage: Record<string, CoverageItem>;
  preflightStatus: Record<string, 'passed' | 'failed' | 'repairing'>;
  canaryResults: Record<string, { records: number; transformErrors: number }>;
  cacheEndpoints: Record<string, CacheEndpointInfo>;
  // Live tail of all HTTP requests across endpoints, newest first. Bounded.
  cacheRequests: CacheRequestEvent[];
  sessionCompleted: SessionCompletedSummary | null;
  recipeYaml: string | null;
  recipeId: string | null;
  done: boolean;
  error: string | null;
}

const INITIAL_STEPS: Record<ScopeStepName, ScopeStepStatus> = {
  connect: 'pending', login: 'pending', navigate: 'pending', extract: 'pending', generate: 'pending', review: 'pending',
};
const INITIAL_PHASES: Record<ScopePhase, ScopePhaseStatus> = {
  auth: 'pending', navigator: 'pending', extractor: 'pending', preflight: 'pending', canary: 'pending',
};

export function useScopeStream(sessionId: string | null): UseScopeStreamResult {
  const [phases, setPhases] = useState<Record<ScopePhase, ScopePhaseStatus>>(INITIAL_PHASES);
  const [steps, setSteps] = useState<Record<ScopeStepName, ScopeStepStatus>>(INITIAL_STEPS);
  const [screenshots, setScreenshots] = useState<ScopeScreenshot[]>([]);
  const [logs, setLogs] = useState<ScopeLogLine[]>([]);
  const [actions, setActions] = useState<ScopeAction[]>([]);
  const [budget, setBudget] = useState<ScopeBudget | null>(null);
  const [coverage, setCoverage] = useState<Record<string, CoverageItem>>({});
  const [preflightStatus, setPreflightStatus] = useState<Record<string, 'passed' | 'failed' | 'repairing'>>({});
  const [canaryResults, setCanaryResults] = useState<Record<string, { records: number; transformErrors: number }>>({});
  const [cacheEndpoints, setCacheEndpoints] = useState<Record<string, CacheEndpointInfo>>({});
  const [cacheRequests, setCacheRequests] = useState<CacheRequestEvent[]>([]);
  const [sessionCompleted, setSessionCompleted] = useState<SessionCompletedSummary | null>(null);
  const [recipeYaml, setRecipeYaml] = useState<string | null>(null);
  const [recipeId, setRecipeId] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const seq = useRef(0);

  useEffect(() => {
    if (!sessionId) return;
    const es = new EventSource(`/api/sessions/${sessionId}/scope/stream`);

    es.onmessage = (ev: MessageEvent) => {
      try {
        const msg = JSON.parse(ev.data as string) as { type: string; payload: Record<string, unknown> };
        switch (msg.type) {
          case 'scope.step': {
            const { step, status } = msg.payload as { step: ScopeStepName; status: ScopeStepStatus };
            setSteps((p) => ({ ...p, [step]: status }));
            break;
          }
          case 'scope.phase': {
            const { phase, status } = msg.payload as { phase: ScopePhase; status: ScopePhaseStatus };
            setPhases((p) => ({ ...p, [phase]: status }));
            break;
          }
          case 'scope.screenshot': {
            const { step, phase, url, caption } = msg.payload as { step?: ScopeStepName; phase?: ScopePhase; url: string; caption?: string };
            setScreenshots((p) => [...p, { step, phase, url, caption }]);
            break;
          }
          case 'scope.log': {
            const { line } = msg.payload as { line: string };
            setLogs((p) => [...p, { line, id: ++seq.current }]);
            break;
          }
          case 'scope.action': {
            const { phase, action, intent, safety } = msg.payload as { phase: ScopePhase; action: string; intent: string; safety: { decision: 'allow' | 'deny'; reason?: string } };
            setActions((p) => [...p, { phase, action, intent, decision: safety.decision, reason: safety.reason, id: ++seq.current }]);
            break;
          }
          case 'scope.budget': {
            setBudget(msg.payload as unknown as ScopeBudget);
            break;
          }
          case 'scope.coverage': {
            const { type, status, endpointName } = msg.payload as { type: string; status: 'pending' | 'found' | 'not_exposed'; endpointName?: string | null };
            setCoverage((p) => ({ ...p, [type]: { type, status, endpointName: endpointName ?? null } }));
            break;
          }
          case 'scope.preflight': {
            const { endpoint, status } = msg.payload as { endpoint: string; status: 'passed' | 'failed' | 'repairing' };
            setPreflightStatus((p) => ({ ...p, [endpoint]: status }));
            break;
          }
          case 'scope.canary': {
            const { endpoint, records, transformErrors } = msg.payload as { endpoint: string; records: number; transformErrors: number };
            setCanaryResults((p) => ({ ...p, [endpoint]: { records, transformErrors } }));
            break;
          }
          case 'cache.endpoint.started': {
            const { endpoint } = msg.payload as { endpoint: string };
            setCacheEndpoints((p) => ({ ...p, [endpoint]: { status: 'started', page: 0, recordsFetched: 0 } }));
            break;
          }
          case 'cache.endpoint.progress': {
            const { endpoint, page, recordsFetched } = msg.payload as { endpoint: string; page: number; recordsFetched: number };
            setCacheEndpoints((p) => {
              const prev = p[endpoint] ?? { status: 'progress' as CacheEndpointStatus, page: 0, recordsFetched: 0 };
              return { ...p, [endpoint]: { ...prev, status: 'progress', page, recordsFetched } };
            });
            break;
          }
          case 'cache.endpoint.finished': {
            const { endpoint, status, recordsFetched, durationMs, error: errMsg } = msg.payload as {
              endpoint: string;
              status: CacheEndpointStatus;
              recordsFetched: number;
              durationMs: number;
              error?: string;
            };
            setCacheEndpoints((p) => {
              const prev = p[endpoint] ?? { page: 0, recordsFetched: 0 };
              return { ...p, [endpoint]: { ...prev, status, recordsFetched, durationMs, error: errMsg } };
            });
            break;
          }
          case 'cache.request': {
            const p = msg.payload as {
              endpoint: string;
              page: number;
              method: string;
              url: string;
              status: number;
              durationMs: number;
              bytes: number;
              recordsFetched: number;
            };
            const reqEvent: CacheRequestEvent = {
              id: ++seq.current,
              ts: Date.now(),
              method: p.method,
              url: p.url,
              status: p.status,
              durationMs: p.durationMs,
              bytes: p.bytes,
              page: p.page,
              recordsFetched: p.recordsFetched,
            };
            // Top-level tail (newest first), capped at 200 entries to keep
            // memory bounded even on long-running imports.
            setCacheRequests((prev) => [reqEvent, ...prev].slice(0, 200));
            // Per-endpoint tail (newest first), capped at 10 entries
            setCacheEndpoints((prev) => {
              const cur = prev[p.endpoint] ?? { status: 'progress' as CacheEndpointStatus, page: 0, recordsFetched: 0 };
              const recent = [reqEvent, ...(cur.recentRequests ?? [])].slice(0, 10);
              return {
                ...prev,
                [p.endpoint]: {
                  ...cur,
                  recentRequests: recent,
                  totalRequests: (cur.totalRequests ?? 0) + 1,
                  totalBytes: (cur.totalBytes ?? 0) + p.bytes,
                },
              };
            });
            break;
          }
          case 'scope.recipe': {
            const { yaml, recipeId: id } = msg.payload as { yaml: string; recipeId: string };
            setRecipeYaml(yaml);
            setRecipeId(id);
            break;
          }
          case 'scope.done': {
            const { status, message } = msg.payload as { status: 'success' | 'error'; message?: string };
            setDone(true);
            if (status === 'error') setError(message ?? 'scoping failed');
            break;
          }
          case 'canonical.endpoint.finished': {
            const { endpoint, resourceType, okCount, errCount } = msg.payload as {
              endpoint: string; resourceType: string; okCount: number; errCount: number;
            };
            setCacheEndpoints((p) => {
              const prev = p[endpoint] ?? { status: 'ok' as CacheEndpointStatus, page: 0, recordsFetched: 0 };
              return {
                ...p,
                [endpoint]: { ...prev, canonicalOkCount: okCount, canonicalErrCount: errCount, resourceType },
              };
            });
            break;
          }
          case 'session.completed': {
            const summary = msg.payload as unknown as SessionCompletedSummary;
            setSessionCompleted(summary);
            break;
          }
        }
      } catch {
        // ignore parse errors
      }
    };

    es.onerror = () => {
      // EventSource auto-reconnects; only mark error after extended period
    };

    return () => es.close();
  }, [sessionId]);

  // REST hydration: fetch the persisted recipe alongside SSE so the panel renders
  // even if event replay misses something or the events table was pruned.
  // SSE-driven updates take precedence (set first, REST won't overwrite).
  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    void (async () => {
      try {
        const data = await api.getScopeRecipe(sessionId);
        if (cancelled) return;
        setRecipeYaml((cur) => cur ?? data.recipe.yaml);
        setRecipeId((cur) => cur ?? data.recipe.id);
      } catch {
        // 404 is normal when the session has no recipe yet — silently ignore
      }
    })();
    return () => { cancelled = true; };
  }, [sessionId]);

  // REST hydration for the import dashboard: when the page loads mid-import
  // (or after any data has been cached), the SSE feed only carries new events,
  // so the dashboard would otherwise sit empty even though raw_cache has rows.
  // Pull the cache summary on mount to seed cacheEndpoints with real counts;
  // SSE updates take precedence afterwards. Refetch on a slow interval so the
  // page stays accurate even if some events were dropped.
  useEffect(() => {
    if (!sessionId) return;
    const sid = sessionId;
    let cancelled = false;
    async function hydrate() {
      try {
        const [data, network] = await Promise.all([
          api.getCacheSummary(sid),
          // Also pull the most-recent network log so per-endpoint expanders
          // show real HTTP history, not "no requests captured yet"
          api.getCacheNetwork(sid, { limit: 60 }).catch(() => ({ requests: [] })),
        ]);
        if (cancelled) return;
        // Group network rows by endpoint, take 10 most-recent each (already
        // newest-first from the API).
        const recentByEndpoint = new Map<string, typeof network.requests>();
        for (const r of network.requests) {
          const arr = recentByEndpoint.get(r.endpoint) ?? [];
          if (arr.length < 10) {
            arr.push(r);
            recentByEndpoint.set(r.endpoint, arr);
          }
        }
        // Build a cacheEndpoints map from the summary's perspective. We treat
        // each imports row as the source of truth for state + record count;
        // canonical counts come from canonByResourceType keyed by endpoint
        // via the imports.endpoint match.
        const rawByEndpoint = new Map<string, number>();
        for (const r of data.rawByEndpoint) rawByEndpoint.set(r.endpoint, Number(r.count));
        setCacheEndpoints((prev) => {
          const next = { ...prev };
          for (const imp of data.imports) {
            const status: CacheEndpointStatus = imp.status === 'completed' ? 'ok'
              : imp.status === 'failed' || imp.status === 'auth_failed' || imp.status === 'rescope_triggered' ? (imp.status as CacheEndpointStatus)
              : imp.status === 'running' ? 'progress' : 'started';
            const existing = next[imp.endpoint];
            const restRequests = recentByEndpoint.get(imp.endpoint) ?? [];
            const hydratedRecent: CacheRequestEvent[] = restRequests.map((r) => ({
              id: Number(r.id),
              ts: new Date(r.fetchedAt).getTime(),
              method: r.method,
              url: r.url,
              status: r.status,
              durationMs: r.durationMs,
              bytes: r.bytes,
              page: 0,
              recordsFetched: 0,
            }));
            // SSE-derived state wins for live counters; merge in REST recent
            // requests so the expander has content to show.
            if (existing && existing.recordsFetched > 0) {
              if ((existing.recentRequests?.length ?? 0) === 0 && hydratedRecent.length > 0) {
                next[imp.endpoint] = { ...existing, recentRequests: hydratedRecent };
              }
              continue;
            }
            const totalBytes = restRequests.reduce((s, r) => s + (r.bytes ?? 0), 0);
            next[imp.endpoint] = {
              status,
              page: 0,
              recordsFetched: rawByEndpoint.get(imp.endpoint) ?? imp.totalFetched ?? 0,
              error: imp.errorDetail?.message,
              recentRequests: hydratedRecent.length > 0 ? hydratedRecent : undefined,
              totalRequests: hydratedRecent.length > 0 ? hydratedRecent.length : undefined,
              totalBytes: totalBytes > 0 ? totalBytes : undefined,
            };
          }
          return next;
        });
      } catch {
        // 404 (session not found) or 401 — silently ignore
      }
    }
    void hydrate();
    const t = setInterval(() => { void hydrate(); }, 5_000);
    return () => { cancelled = true; clearInterval(t); };
  }, [sessionId]);

  return { phases, steps, screenshots, logs, actions, budget, coverage, preflightStatus, canaryResults, cacheEndpoints, cacheRequests, sessionCompleted, recipeYaml, recipeId, done, error };
}
